import { Hanko, register } from '@teamhanko/hanko-elements';
import { all as hankoTranslations } from '@teamhanko/hanko-elements/i18n/all';
import { runtimeConfig } from '@/runtime-config';

const HANKO_API_URL = runtimeConfig.hankoApiUrl;
const COOKIE_NAME = 'hanko';

let hanko: Hanko | null = null;

export function initHanko(initialLang?: string): void {
  if (!HANKO_API_URL) {
    return;
  }
  // `lang` is forwarded to the X-Language header on every request the SDK makes — Hanko
  // Cloud uses it to localise magic-link / passcode emails. Independently, the <hanko-auth>
  // web component reads `document.documentElement.lang` for its own UI labels.
  hanko = new Hanko(HANKO_API_URL, initialLang ? { lang: initialLang } : undefined);
  void register(HANKO_API_URL, {
    translations: hankoTranslations,
    fallbackLanguage: 'de',
  }).catch((error) => {
    console.error('Failed to register Hanko web components', error);
  });
}

/** Switch the language Hanko sends with API requests at runtime (see i18n/locale.tsx). */
export function setHankoLang(lang: string): void {
  hanko?.setLang(lang);
}

export function getHanko(): Hanko {
  if (!hanko) {
    throw new Error('Hanko is not initialised. Set VITE_HANKO_API_URL and call initHanko() at startup.');
  }
  return hanko;
}

function readCookie(name: string): string | null {
  const cookies = document.cookie.split('; ');
  const match = cookies.find((cookie) => cookie.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export async function getAccessToken(): Promise<string | null> {
  // Hanko stores the session JWT in a cookie (default name "hanko"). It is not HttpOnly so the
  // frontend can read it and send it as a Bearer token to the backend — the standard pattern
  // for cross-origin API calls (app.clockoo.io -> api.clockoo.io).
  return readCookie(COOKIE_NAME);
}

export async function isAuthenticated(): Promise<boolean> {
  if (!hanko) return false;
  try {
    const response = await hanko.sessionClient.validate();
    return response.is_valid === true;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  try {
    await hanko?.user.logout();
  } finally {
    window.location.assign('/login');
  }
}
